import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Lock, Download, Play, X, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { SuiteLayout, Glass, NeonButton, GhostButton } from "../../components/suite/SuiteLayout";
import { CreditsHud } from "../../components/suite/CreditsHud";
import { BatchConfirm } from "../../components/suite/BatchConfirm";
import { Plans, usePlan, errText } from "../../lib/plans";
import { buildFilename } from "../../lib/storage";
import { processImage } from "../../lib/batchProcess";
import { buildBatchZip } from "../../lib/batchExport";
import { AiCopyEditor } from "../../components/suite/AiCopyEditor";

const SPEEDS = [
  { id: "basic", label: "Basic", desc: "1 file at a time", tier: "All plans" },
  { id: "fast", label: "Fast Queue", desc: "3 in parallel", tier: "Creator+" },
  { id: "priority", label: "Priority Queue", desc: "6 in parallel", tier: "Pro Suite" },
];

export default function SuiteBatch() {
  const { plan, set: setPlan } = usePlan();
  const [files, setFiles] = useState([]);
  const [prefix, setPrefix] = useState("BATCH");
  const [tags, setTags] = useState("product");
  const [speed, setSpeed] = useState("basic");
  const [confirm, setConfirm] = useState(false);
  const [rows, setRows] = useState({});
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [zipping, setZipping] = useState(false);
  const [drag, setDrag] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [useAi, setUseAi] = useState(false);
  const cost = files.length * (useAi ? 2 : 1);
  const input = useRef(null);
  const allowed = plan?.speeds || [];

  const add = (list) => {
    const imgs = [...list].filter((f) => f.type.startsWith("image/"));
    if (imgs.length < list.length) toast.message(`${list.length - imgs.length} non-image file(s) skipped`);
    setFiles((prev) => [...(processed ? [] : prev), ...imgs].slice(0, 500));
    setProcessed(false);
    setResults([]);
    setRows({});
  };

  const run = async () => {
    setConfirm(false);
    let res;
    try {
      res = await Plans.consume(files.length, speed, `${prefix} · ${files.length} files${useAi ? " · AI" : ""}`, useAi);
    } catch (e) {
      toast.error(errText(e, "Could not reserve credits"));
      return;
    }
    setPlan(res);
    setRunning(true);
    try {
      await processAll(res);
    } catch (e) {
      toast.error(errText(e, "Batch stopped unexpectedly"));
    } finally {
      setRunning(false);
      setProcessed(true);
    }
  };

  const processAll = async (res) => {
    const out = [];
    let failed = 0;
    let refundable = 0;
    let next = 0;
    const worker = async () => {
      while (next < files.length) {
        const i = next++;
        const f = files[i];
        const name = buildFilename(prefix, tags, i + 1);
        setRows((r) => ({ ...r, [i]: { state: "working", name } }));
        try {
          const describe = useAi ? (b64) => Plans.describe(res.batch_id, name, b64) : null;
          const meta = { sessionName: prefix, tags, sequence: i + 1, filename: name, source: "batch", software: "AI Image Suites / Batch Files" };
          const res1 = await processImage(f, meta, describe);
          out.push({ index: i, name, meta, blob: res1.blob, ai: res1.ai, original: res1.ai, edited: false });
          if (res1.aiFailed) refundable += 1;
          setRows((r) => ({ ...r, [i]: { state: "done", name, ai: res1.ai, aiFailed: res1.aiFailed } }));
        } catch {
          failed += 1;
          refundable += useAi ? 2 : 1;
          setRows((r) => ({ ...r, [i]: { state: "failed", name } }));
        }
      }
    };
    await Promise.all(Array.from({ length: res.concurrency }, worker));
    if (refundable) {
      setPlan(await Plans.refund(res.batch_id, refundable));
      toast.message(`${refundable} credit(s) refunded for files that could not be processed`);
    }
    setResults(out.sort((a, b) => a.index - b.index));
    toast.success(`Batch done · ${files.length - failed} files`);
  };

  const editAi = (index, ai, reset = false) =>
    setResults((list) => list.map((r) => (r.index === index ? { ...r, ai, edited: !reset } : r)));

  const download = async () => {
    setZipping(true);
    let blob;
    try { blob = await buildBatchZip(prefix, results); } catch { toast.error("Could not build the ZIP"); return; } finally { setZipping(false); }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${prefix || "batch"}.zip`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  const done = Object.values(rows).filter((r) => r.state === "done").length;
  return (
    <SuiteLayout credits={plan?.available} title="Batch Processing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-testid="suite-batch">
        <p className="font-mono text-xs tracking-[0.3em] text-cyan-400">BATCH FILES</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Batch Processing</h1>
        <div className="mt-8"><CreditsHud plan={plan} compact /></div>
        <div className="mt-6 grid lg:grid-cols-[1.3fr_1fr] gap-6">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
            onClick={() => input.current?.click()}
            className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? "border-cyan-400 bg-cyan-400/10" : "border-white/15 bg-slate-950/50 hover:border-cyan-400/50"}`}
            data-testid="batch-upload-zone">
            <UploadCloud className="mx-auto text-cyan-300" size={42} />
            <p className="mt-4 font-display text-xl font-bold text-white">Drop images here or click to choose</p>
            <p className="text-sm text-slate-400 mt-1">JPEG, PNG, WebP, HEIC (where your browser can open it) · up to 500 files</p>
            <input ref={input} type="file" multiple accept="image/*" className="hidden" onChange={(e) => add(e.target.files)} data-testid="batch-file-input" />
            {files.length > 0 && <p className="mt-4 font-mono text-sm text-emerald-300" data-testid="batch-file-count">{files.length} file{files.length === 1 ? "" : "s"} ready</p>}
          </div>
          <Glass className="p-6 space-y-5" data-testid="batch-console">
            <p className="font-mono text-[11px] tracking-[0.25em] text-purple-300">ACTION CONSOLE</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-slate-400">Name prefix
                <input value={prefix} onChange={(e) => setPrefix(e.target.value)} maxLength={40} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white font-mono text-sm focus:border-cyan-400 outline-none" data-testid="batch-prefix-input" />
              </label>
              <label className="text-xs text-slate-400">Tags
                <input value={tags} onChange={(e) => setTags(e.target.value)} maxLength={60} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white font-mono text-sm focus:border-cyan-400 outline-none" data-testid="batch-tags-input" />
              </label>
            </div>
            <p className="text-xs font-mono text-slate-500 truncate" data-testid="batch-name-preview">→ {buildFilename(prefix, tags, 1)}</p>
            <div className="grid gap-2">
              {SPEEDS.map((s) => {
                const locked = !allowed.includes(s.id);
                return (
                  <button key={s.id} disabled={locked || running} onClick={() => setSpeed(s.id)} data-testid={`batch-speed-${s.id}`}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${speed === s.id && !locked ? "border-cyan-400 bg-cyan-400/10" : "border-white/10 hover:border-white/30"} disabled:opacity-45`}>
                    <span><span className="text-white font-semibold">{s.label}</span><span className="block text-xs text-slate-400">{s.desc}</span></span>
                    <span className="flex items-center gap-1 text-[11px] font-mono text-slate-400">{locked && <Lock size={12} />} {s.tier}</span>
                  </button>
                );
              })}
            </div>
            <label className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${useAi ? "border-purple-400 bg-purple-500/10" : "border-white/10 hover:border-white/30"}`} data-testid="batch-ai-toggle">
              <input type="checkbox" checked={useAi} disabled={running} onChange={(e) => setUseAi(e.target.checked)} className="mt-1 accent-purple-500" data-testid="batch-ai-checkbox" />
              <span>
                <span className="flex items-center gap-1.5 text-white font-semibold"><Sparkles size={14} className="text-purple-300" /> AI title, tags & alt text</span>
                <span className="block text-xs text-slate-400">+1 credit per image · saved in each file's EXIF and a product-descriptions.csv in the ZIP</span>
              </span>
            </label>
            <NeonButton className="w-full" disabled={!files.length || running || !plan || processed} onClick={() => setConfirm(true)} data-testid="batch-run-btn">
              <Play size={16} /> {running ? `Processing ${done}/${files.length}` : processed ? "Batch complete — add new files" : files.length ? `Process ${files.length} file${files.length === 1 ? "" : "s"} · ${cost} credits` : "Add files to start"}
            </NeonButton>
            {processed && results.length > 0 && <GhostButton className="w-full" onClick={download} disabled={zipping} data-testid="batch-download-zip"><Download size={16} /> {zipping ? "Building ZIP…" : "Download ZIP"}</GhostButton>}
            {processed && results.some((r) => r.ai) && <p className="text-[11px] text-slate-500" data-testid="batch-zip-contents">ZIP includes product-descriptions.csv and shopify-products.csv (draft products, add prices in Shopify).</p>}
            {files.length > 0 && !running && <button onClick={() => { setFiles([]); setRows({}); setResults([]); setProcessed(false); }} className="text-xs text-slate-500 hover:text-white flex items-center gap-1" data-testid="batch-clear-btn"><X size={12} /> Clear files</button>}
          </Glass>
        </div>
        {files.length > 0 && (
          <Glass className="mt-6 max-h-80 overflow-auto divide-y divide-white/5" data-testid="batch-file-list">
            {files.map((f, i) => {
              const r = rows[i];
              const ai = results.find((x) => x.index === i)?.ai || r?.ai;
              return (
                <div key={`${f.name}-${i}`} className="px-5 py-2.5 flex items-center justify-between text-sm gap-4" data-testid={`batch-row-${i}`}>
                  <span className="truncate text-slate-300">{f.name}</span>
                  <span className="font-mono text-xs flex items-center gap-1.5 shrink-0">
                    {r?.state === "done" && <><CheckCircle2 size={14} className="text-emerald-400" /><span className="text-emerald-300">{r.name}</span>
                      {ai && <span className="text-purple-300 max-w-[220px] truncate" title={ai.alt_text} data-testid={`batch-row-ai-${i}`}>· {ai.title}</span>}
                      {r.aiFailed && <span className="text-amber-300">· AI failed, refunded</span>}</>}
                    {r?.state === "failed" && <><AlertTriangle size={14} className="text-amber-400" /><span className="text-amber-300">failed · refunded</span></>}
                    {r?.state === "working" && <span className="text-cyan-300 animate-pulse">processing…</span>}
                    {!r && <span className="text-slate-500">queued</span>}
                  </span>
                </div>
              );
            })}
          </Glass>
        )}
        {processed && <AiCopyEditor results={results} onChange={editAi} />}
      </div>
      <BatchConfirm open={confirm} count={cost} plan={plan} ai={useAi} images={files.length} onCancel={() => setConfirm(false)} onConfirm={run} />
    </SuiteLayout>
  );
}
